import type { ReactNode } from 'react'
import { useLocation } from 'react-router'
import type { InterpolationFunction } from 'styled-components'

import { SideNavigation, TopNavigation, useSideNavigation } from '@loadsmart/loadsmart-ui'
import { Layout, Text } from '@loadsmart/miranda-react'

import {
  PORTAL_LINK_BIDS_PATH,
  PORTAL_LINK_RFP_PATH,
  PORTAL_LINK_SHIPMENT_PATH,
  PORTAL_LINK_SHIPMENTS_PATH,
  PORTAL_LINK_SHIPPERS_PATH,
  PORTAL_LINK_TENDER_PATH,
} from 'rate-command-center/ui/PortalLinkRoute'

import shipperGuideDarkLogo from '../../assets/shipperguide-logo-dark.svg'
import shipperGuideDefaultLogo from '../../assets/shipperguide-logo.svg'
import {
  StyledMainWrapper,
  StyledPageWIthSideNav,
  StyledSideNavigationWrapper,
  StyledTopNavigationWrapper,
} from './styles'

interface LoggedOutNavigationProps {
  readonly children: ReactNode
  readonly color?: string
  readonly padding?: string | InterpolationFunction<any>
}

function SideNavCollapseWrapper({
  open,
  children,
}: {
  readonly open: boolean
  readonly children: ReactNode
}) {
  return <StyledSideNavigationWrapper $open={open}>{children}</StyledSideNavigationWrapper>
}

function TopNavCollapseWrapper({ children }: { readonly children: ReactNode }) {
  return <StyledTopNavigationWrapper>{children}</StyledTopNavigationWrapper>
}

function NavigationLogo({ navPosition }: { readonly navPosition: 'side' | 'top' }) {
  return (
    <img
      src={navPosition === 'side' ? shipperGuideDefaultLogo : shipperGuideDarkLogo}
      width={150}
      alt="ShipperGuide"
    />
  )
}

function isActivePath(path: string, pathname: string) {
  return pathname.startsWith(path)
}

function LoggedOutNavigation({ children, color, padding }: LoggedOutNavigationProps) {
  const { open, hide, toggle } = useSideNavigation()
  const { search, pathname } = useLocation()
  const portalLinkBidsUrl = `/#${PORTAL_LINK_BIDS_PATH}${search}`
  const portalLinkTenderUrl = `/#${PORTAL_LINK_TENDER_PATH}${search}`
  const portalLinkRfpUrl = `/#${PORTAL_LINK_RFP_PATH}${search}`
  const portalLinkShipmentsUrl = `/#${PORTAL_LINK_SHIPMENTS_PATH}${search}`
  const portalLinkShippersUrl = `/#${PORTAL_LINK_SHIPPERS_PATH}${search}`

  return (
    <StyledPageWIthSideNav>
      <SideNavCollapseWrapper open={open}>
        <SideNavigation>
          <SideNavigation.CloseButton size={24} onClick={hide} />
          <SideNavigation.Logo url={portalLinkBidsUrl}>
            <NavigationLogo navPosition="side" />
          </SideNavigation.Logo>

          <SideNavigation.Menu>
            <SideNavigation.Menu.Item
              active={isActivePath(PORTAL_LINK_SHIPMENT_PATH, pathname)}
              label="Shipments"
              url={portalLinkShipmentsUrl}
            />
            <SideNavigation.Menu.Item
              active={isActivePath(PORTAL_LINK_BIDS_PATH, pathname)}
              label="Bids"
              url={portalLinkBidsUrl}
            />
            <SideNavigation.Menu.Item
              active={isActivePath(PORTAL_LINK_TENDER_PATH, pathname)}
              label="Tenders"
              url={portalLinkTenderUrl}
            />
            <SideNavigation.Menu.Item
              active={isActivePath(PORTAL_LINK_RFP_PATH, pathname)}
              label="RFPs"
              url={portalLinkRfpUrl}
            />
            <SideNavigation.Menu label="manage">
              <SideNavigation.Menu.Item
                active={isActivePath(PORTAL_LINK_SHIPPERS_PATH, pathname)}
                label="Shippers"
                url={portalLinkShippersUrl}
              />
            </SideNavigation.Menu>
          </SideNavigation.Menu>
          {process.env.REACT_APP_RELEASE_VERSION && (
            <Layout.Box
              padding="none"
              paddingL="spacing-6"
              paddingB="spacing-4"
              style={{ marginTop: 'auto' }}
            >
              <Text as="p" variant="body-sm" color="color-text-tertiary">
                v{process.env.REACT_APP_RELEASE_VERSION}
              </Text>
            </Layout.Box>
          )}
        </SideNavigation>
      </SideNavCollapseWrapper>
      <StyledMainWrapper color={color} padding={padding}>
        <TopNavCollapseWrapper>
          <TopNavigation>
            <TopNavigation.OpenSideNavButton onClick={toggle} />
            <TopNavigation.Logo url={`/#${PORTAL_LINK_BIDS_PATH}`}>
              <NavigationLogo navPosition="top" />
            </TopNavigation.Logo>
            <TopNavigation.Menu align="right">
              <TopNavigation.Menu.Item label={''}></TopNavigation.Menu.Item>
            </TopNavigation.Menu>
          </TopNavigation>
        </TopNavCollapseWrapper>
        <main>{children}</main>
      </StyledMainWrapper>
    </StyledPageWIthSideNav>
  )
}

export default LoggedOutNavigation
